import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { LayoutManagementBase } from '@shared-feature/components/layout-management-base/layout-management-base';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Column } from 'devextreme/ui/data_grid';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { BlobPart } from 'openai/uploads';

import { DateFormatService } from 'src/app/shared/services/date-format.service';

@Injectable()
export abstract class DevExpressGridDetailsBase extends LayoutManagementBase {
  protected dateFormatService = inject(DateFormatService);

  dxDataSource: any;
  dxColumns = signal<Column[]>([]);
  pageSize = signal(100);
  dxGridSignal = signal<DxDataGridComponent | undefined>(undefined);
  dxGridInitialized = signal<boolean>(false);

  protected get dateFormat(): string {
    return this.dateFormatService.getDefaultDateFormat();
  }

  mappedDxColumns: Signal<Column[]> = computed(() => {
    const dateFormat = this.dateFormat;
    const columns = this.dxColumns();
    return this.applyDateFormat(columns, dateFormat);
  });

  /**
   * Apply date format to all date columns that don't already have a format
   */
  protected applyDateFormat(columns: Column[], dateFormat: string): Column[] {
    return columns.map(col => {
      if (col.dataType === 'date' && !col.format) {
        return { ...col, format: dateFormat };
      }
      return col;
    });
  }

  onDxGridInitialized(e: any) {
    this.dxGridSignal.set(e.component);
    this.dxGridInitialized.set(true);
    this.dateFormatService.configureDevExpressLocale();
    setTimeout(() => {
      e.component?.refresh?.();
    }, 0);
  }

  onDxExporting(e: any) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.getComponentName());
    const gridInstance = e.component;

    exportDataGrid({
      component: gridInstance,
      worksheet: worksheet,
      autoFilterEnabled: true
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, `${this.getComponentName()}.xlsx`);
      });
    });

    e.cancel = true;
  }

  /**
   * Initialize data source with a local array for client-side filtering, sorting, grouping, and paging.
   * Call this when the data array is available (e.g. in ngOnInit) and again when the array reference changes.
   * @param data The array to bind (e.g. srv.knockoffItems)
   * @param key Optional key field for the store (default 'docId'); use a unique field if available
   */
  initDxDataSource(data: any[], key: string = 'docId'): void {
    this.dxDataSource = {
      store: {
        type: 'array',
        data: data ?? [],
        key: key
      },
      paginate: true,
      pageSize: this.pageSize()
    };
  }

  /**
   * Refresh the grid (e.g. after setPayment or reloadData when the same array reference is mutated).
   * Safe to call before the grid is initialized (e.g. from writeValue); no-op until grid exists.
   */
  refreshGridData(): void {
    const grid = this.dxGridSignal();
    if (grid?.instance && typeof grid.instance.refresh === 'function') {
      grid.instance.refresh();
    }
  }

  protected override getResetColumns(columns?: any): any[] {
    return columns ?? this.mappedDxColumns();
  }
}
